import { Controller } from './angular-entities';
import angular from 'angular';

export interface IComponentControllerScope<State = any> extends angular.IScope {
    onReady?: Function;
    state?: State;
    isReady?: boolean;
}

export class ComponentController extends Controller {
    private _waiters: Promise<void>[];
    private _ready: Promise<void>;
    private _resolveReady: (value: void | PromiseLike<void>) => void;
    private _alreadyRun: boolean;
    private _cancelPromise: Promise<void>;

    constructor(protected $scope: IComponentControllerScope) {
        super($scope);
        this._waiters = [];
        this._ready = new Promise(resolve => {
            this._resolveReady = resolve;
        });
        this.$scope.isReady = false;
    }

    public get ready(): Promise<void> {
        return this._ready;
    }

    protected get cancelPromise() {
        if (!this._cancelPromise) {
            this._cancelPromise = new Promise<void>(resolve => {
                this.$scope.$on('$destroy', () => {
                    resolve();
                });
            });
        }
        return this._cancelPromise;
    }

    /**
     * Pushes the promise to the queue.
     * The queue gets resolved when the {@link run} function gets called.
     * @param promise should get added to the queue (and wait till it gets resolved on run)
     */
    protected waitFor(promise: Promise<any>): void {
        if (this._alreadyRun) {
            console.error('_waitFor after run assignment - incorrect usage');
        }
        this._waiters.push(promise);
    }

    protected waitForScopeVariable(variable: string): void {
        this.waitFor(
            new Promise<void>(resolve => {
                const unsubscribe = this.$scope.$watch(variable, value => {
                    if (value) {
                        unsubscribe();
                        resolve();
                    }
                });
            })
        );
    }

    /**
     * Resolves the promises that are in the waiting queue.
     * Resolves the 'ready' promise reference.
     * calls {@link start}
     */
    protected run() {
        this._alreadyRun = true;
        Promise.all(this._waiters)
            .then(() => {
                this._resolveReady();
                this.start();
                this.$scope.$applyAsync();
            })
            .catch(error => {
                this.start();
                this.$scope.$applyAsync();
                throw error;
            });
    }

    protected get state() {
        return this.$scope.state;
    }

    /**
     * Passes context to the api when ready.
     * Starts component and sets the state as ready.
     */
    protected start() {
        const $scope = this.$scope;
        if ($scope.onReady) {
            // support for deprecated scope definition {onReady: '@'}
            if (typeof $scope.onReady === 'string') {
                console.error('deprecated @onReady scope definition', this);
                $scope.$parent.$eval($scope.onReady, { api: this });
            } else if (typeof $scope.onReady === 'function') {
                $scope.onReady({ api: this });
            }
        }
        $scope.isReady = true;
    }
}
